import { useMemo, useState } from "react";

type Locale = "en" | "es";
type Currency = "USD" | "EUR" | "GBP" | "CAD" | "AUD" | "JPY" | "INR";
type Term = "1y" | "3y";

type Pricing = {
  oneYear: number;
  threeYear: number;
};

const PLUS_PRICING: Record<Currency, Pricing> = {
  USD: { oneYear: 2500, threeYear: 2300 },
  EUR: { oneYear: 2250, threeYear: 2100 },
  GBP: { oneYear: 1950, threeYear: 1800 },
  CAD: { oneYear: 3650, threeYear: 3400 },
  AUD: { oneYear: 4000, threeYear: 3700 },
  JPY: { oneYear: 398000, threeYear: 368000 },
  INR: { oneYear: 190000, threeYear: 175000 },
};

const CURRENCIES = Object.keys(PLUS_PRICING) as Currency[];
const SHOPIFY_PRICING_SOURCE: Record<Locale, string> = {
  en: "https://help.shopify.com/en/manual/intro-to-shopify/pricing-plans/plans-features/shopify-plus-plan",
  es: "https://help.shopify.com/es/manual/intro-to-shopify/pricing-plans/plans-features/shopify-plus-plan",
};

function parseAmount(value: string) {
  const parsed = Number(value.replace(/[^0-9.]/g, ""));
  return Number.isFinite(parsed) ? Math.max(parsed, 0) : 0;
}

function currencyFormatter(currency: Currency, locale: Locale) {
  return new Intl.NumberFormat(locale === "es" ? "es-MX" : "en-US", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  });
}

export function ShopifyPlusCostCalculator({ locale = "en" }: { locale?: Locale }) {
  const [currency, setCurrency] = useState<Currency>("USD");
  const [term, setTerm] = useState<Term>("3y");
  const [appsMonthly, setAppsMonthly] = useState("0");
  const [engineeringMonthly, setEngineeringMonthly] = useState("0");
  const [systemsAnnual, setSystemsAnnual] = useState("0");
  const [implementationOneTime, setImplementationOneTime] = useState("0");

  const monthlyPlatform = term === "3y" ? PLUS_PRICING[currency].threeYear : PLUS_PRICING[currency].oneYear;
  const formatter = useMemo(() => currencyFormatter(currency, locale), [currency, locale]);

  const totals = useMemo(() => {
    const platformAnnual = monthlyPlatform * 12;
    const recurringAnnual =
      platformAnnual +
      parseAmount(appsMonthly) * 12 +
      parseAmount(engineeringMonthly) * 12 +
      parseAmount(systemsAnnual);
    const firstYear = recurringAnnual + parseAmount(implementationOneTime);
    const planning36 = recurringAnnual * 3 + parseAmount(implementationOneTime);

    return { platformAnnual, recurringAnnual, firstYear, planning36 };
  }, [monthlyPlatform, appsMonthly, engineeringMonthly, systemsAnnual, implementationOneTime]);

  const t =
    locale === "es"
      ? {
          eyebrow: "Calculadora de TCO",
          title: "Estima el costo real de Shopify Plus",
          intro:
            "Usa el precio oficial actual de Plus como base y añade tus propios costos operativos. Los campos empiezan en cero para no asumir el gasto de tu negocio. Esto es una estimación de planificación, no una cotización de Shopify ni de Shugert.",
          currency: "Moneda de facturación",
          term: "Plazo",
          oneYear: "1 año",
          threeYear: "3 años",
          apps: "Apps / mes",
          engineering: "Ingeniería y soporte / mes",
          systems: "Integraciones y sistemas / año",
          implementation: "Migración e implementación (una vez)",
          platformMonthly: "Plataforma / mes",
          platformAnnual: "Plataforma / año",
          recurringAnnual: "TCO recurrente / año",
          firstYear: "TCO estimado del año 1",
          planning36: "Estimación a 36 meses",
          note:
            "No incluye procesamiento de pagos, impuestos ni posibles tarifas variables por volumen o modelo de negocio. La estimación a 36 meses mantiene el precio mensual seleccionado durante todo el periodo.",
          sourcePrefix: "Precio base verificado con Shopify el 25 de septiembre de 2026.",
          sourceLabel: "Ver precio oficial de Shopify Plus",
        }
      : {
          eyebrow: "TCO calculator",
          title: "Estimate the real cost of Shopify Plus",
          intro:
            "Start with Shopify's current published Plus price, then add your own operating costs. Optional cost fields start at zero so the calculator does not assume what your business spends. This is a planning estimate, not a Shopify or Shugert quote.",
          currency: "Billing currency",
          term: "Term",
          oneYear: "1 year",
          threeYear: "3 years",
          apps: "Apps / month",
          engineering: "Engineering & support / month",
          systems: "Integrations & systems / year",
          implementation: "Migration & implementation (one-time)",
          platformMonthly: "Platform / month",
          platformAnnual: "Platform / year",
          recurringAnnual: "Recurring TCO / year",
          firstYear: "Estimated year-one TCO",
          planning36: "36-month planning estimate",
          note:
            "Excludes payment processing, taxes and any variable platform fee that may apply to higher-volume or more complex businesses. The 36-month estimate holds the selected monthly platform price constant for planning purposes.",
          sourcePrefix: "Base platform pricing checked against Shopify on September 25, 2026.",
          sourceLabel: "View Shopify's official Plus pricing",
        };

  const inputClass =
    "mt-2 w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm text-foreground outline-none transition-colors focus:border-foreground/40";

  return (
    <section className="mt-4 rounded-3xl border border-border bg-card/50 p-6 sm:p-8" aria-labelledby="shopify-plus-cost-calculator">
      <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-muted-foreground">{t.eyebrow}</p>
      <h2 id="shopify-plus-cost-calculator" className="mt-3 font-display text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">
        {t.title}
      </h2>
      <p className="mt-3 max-w-3xl text-sm leading-relaxed text-muted-foreground">{t.intro}</p>

      <div className="mt-7 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <label className="text-sm font-medium text-foreground">
          {t.currency}
          <select className={inputClass} value={currency} onChange={(event) => setCurrency(event.target.value as Currency)}>
            {CURRENCIES.map((item) => (
              <option key={item} value={item}>{item}</option>
            ))}
          </select>
        </label>

        <label className="text-sm font-medium text-foreground">
          {t.term}
          <select className={inputClass} value={term} onChange={(event) => setTerm(event.target.value as Term)}>
            <option value="1y">{t.oneYear}</option>
            <option value="3y">{t.threeYear}</option>
          </select>
        </label>

        <MoneyInput label={t.apps} value={appsMonthly} onChange={setAppsMonthly} className={inputClass} />
        <MoneyInput label={t.engineering} value={engineeringMonthly} onChange={setEngineeringMonthly} className={inputClass} />
        <MoneyInput label={t.systems} value={systemsAnnual} onChange={setSystemsAnnual} className={inputClass} />
        <MoneyInput label={t.implementation} value={implementationOneTime} onChange={setImplementationOneTime} className={inputClass} />
      </div>

      <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <Metric label={t.platformMonthly} value={formatter.format(monthlyPlatform)} />
        <Metric label={t.platformAnnual} value={formatter.format(totals.platformAnnual)} />
        <Metric label={t.recurringAnnual} value={formatter.format(totals.recurringAnnual)} />
        <Metric label={t.firstYear} value={formatter.format(totals.firstYear)} emphasized />
        <Metric label={t.planning36} value={formatter.format(totals.planning36)} />
      </div>

      <div className="mt-6 border-t border-border pt-5 text-xs leading-relaxed text-muted-foreground">
        <p>{t.note}</p>
        <p className="mt-2">
          {t.sourcePrefix}{" "}
          <a href={SHOPIFY_PRICING_SOURCE[locale]} target="_blank" rel="noopener noreferrer" className="font-medium text-foreground underline underline-offset-4 hover:opacity-80">
            {t.sourceLabel}
          </a>.
        </p>
      </div>
    </section>
  );
}

function MoneyInput({
  label,
  value,
  onChange,
  className,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  className: string;
}) {
  return (
    <label className="text-sm font-medium text-foreground">
      {label}
      <input
        className={className}
        type="number"
        inputMode="decimal"
        min="0"
        step="100"
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}

function Metric({ label, value, emphasized = false }: { label: string; value: string; emphasized?: boolean }) {
  return (
    <div className={`rounded-2xl border p-4 ${emphasized ? "border-foreground/25 bg-foreground/[0.04]" : "border-border bg-background/50"}`}>
      <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">{label}</p>
      <p className="mt-2 font-display text-xl font-semibold tracking-tight text-foreground">{value}</p>
    </div>
  );
}
